import { FC } from "react";
import { FormattedMessage } from "react-intl";
import {
  FONT_FAMILY_STACKS,
  isFontFamilyOptionKey,
} from "~/features/settings/constants";

type SettingsPreviewProps = {
  fontFamily?: string;
  lineSpacing?: string;
  logo?: string;
  footnote?: string | null;
};

export const SettingsPreview: FC<SettingsPreviewProps> = ({
  fontFamily,
  lineSpacing,
  logo,
  footnote,
}) => {
  const getFontFamilyStyle = () => {
    if (isFontFamilyOptionKey(fontFamily)) {
      return FONT_FAMILY_STACKS[fontFamily];
    }

    return fontFamily || FONT_FAMILY_STACKS.sans;
  };

  const getLineHeightStyle = () => {
    if (!lineSpacing) return { lineHeight: "1.5" };
    return { lineHeight: lineSpacing };
  };

  const lineHeightStyle = getLineHeightStyle();
  const fontFamilyStyle = getFontFamilyStyle();

  return (
    <div
      className="w-full max-w-[400px] flex flex-col gap-2 relative w-full bg-muted rounded-lg overflow-hidden shadow-sm border border-border min-h-[541px]"
      style={{ ...lineHeightStyle, fontFamily: fontFamilyStyle }}
    >
      <div className="bg-card p-6 h-[550px] relative flex flex-col max-h-[550px] text-foreground">
        <div className="flex items-start justify-between mb-[10px] pb-1 flex-shrink-0">
          <div className="text-left flex-shrink-0">
            <p className="text-[8px] text-muted-foreground mt-0.5">
              <FormattedMessage
                id="settings.preview.caseName"
                defaultMessage="Case Name"
              />
              :{" "}
              <FormattedMessage
                id="settings.preview.exampleCase"
                defaultMessage="Example case"
              />
              <br />
              <FormattedMessage
                id="settings.preview.dateOfBirth"
                defaultMessage="Date of Birth"
              />
              : 3/4/1958 <br />
              <FormattedMessage
                id="settings.preview.claimNumber"
                defaultMessage="Claim/Case #"
              />
              : R2515575 <br />
            </p>
          </div>
          <div>
            <img src={logo} alt="Logo" className="h-[30px] object-contain" />
          </div>
        </div>
        <div className="overflow-hidden max-h-[430px] h-[430px]">
          <div
            className="prose prose-xs dark:prose-invert max-w-none"
            style={lineHeightStyle}
          >
            <h2
              style={lineHeightStyle}
              className="text-[16px] font-semibold mb-1 mt-0"
            >
              <FormattedMessage
                id="settings.preview.executiveSummary"
                defaultMessage="Executive Summary"
              />
            </h2>
            <p className="text-[8px] mb-2">
              <FormattedMessage
                id="settings.preview.executiveSummaryText"
                defaultMessage="This comprehensive medical case analysis examines the patient's condition, treatment history, and recommended care plan. The report synthesizes information from multiple medical records, insurance documentation, and expert consultations."
              />
            </p>
            <h3
              style={lineHeightStyle}
              className="text-[12px] font-semibold mb-1 mt-2"
            >
              <FormattedMessage
                id="settings.preview.keyFindings"
                defaultMessage="Key Findings"
              />
            </h3>
            <ul
              style={lineHeightStyle}
              className="text-[8px] mb-2 pl-2 space-y-0.5"
            >
              <li className="mb-0">
                <FormattedMessage
                  id="settings.preview.keyFinding1"
                  defaultMessage="Initial diagnostic imaging revealed significant findings"
                />
              </li>
              <li>
                <FormattedMessage
                  id="settings.preview.keyFinding2"
                  defaultMessage="Treatment protocols established and documented"
                />
              </li>
              <li>
                <FormattedMessage
                  id="settings.preview.keyFinding3"
                  defaultMessage="Ongoing monitoring requirements identified"
                />
              </li>
            </ul>
            <h3
              style={lineHeightStyle}
              className="text-[12px] font-semibold mb-1 mt-2"
            >
              <FormattedMessage
                id="settings.preview.patientInformation"
                defaultMessage="Patient Information"
              />
            </h3>
            <div
              style={lineHeightStyle}
              className="text-[8px] mb-2 space-y-0.5"
            >
              <div>
                <strong>
                  <FormattedMessage
                    id="settings.preview.name"
                    defaultMessage="Name"
                  />
                  :
                </strong>{" "}
                John Smith
              </div>
              <div>
                <strong>
                  <FormattedMessage
                    id="settings.preview.dob"
                    defaultMessage="DOB"
                  />
                  :
                </strong>{" "}
                March 15, 1980
              </div>
              <div>
                <strong>
                  <FormattedMessage
                    id="settings.preview.caseType"
                    defaultMessage="Case Type"
                  />
                  :
                </strong>{" "}
                <FormattedMessage
                  id="settings.preview.personalInjury"
                  defaultMessage="Personal Injury"
                />
              </div>
              <div>
                <strong>
                  <FormattedMessage
                    id="settings.preview.insurance"
                    defaultMessage="Insurance"
                  />
                  :
                </strong>{" "}
                Blue Cross Blue Shield
              </div>
            </div>
            <h3
              style={lineHeightStyle}
              className="text-[12px] font-semibold mb-1 mt-2"
            >
              <FormattedMessage
                id="settings.preview.medicalTimeline"
                defaultMessage="Medical Timeline"
              />
            </h3>
            <ol
              style={lineHeightStyle}
              className="text-[8px] mb-2 pl-4 space-y-0.5"
            >
              <li className="mb-0">
                <FormattedMessage
                  id="settings.preview.timeline1"
                  defaultMessage="Initial consultation and examination"
                />
              </li>
              <li>
                <FormattedMessage
                  id="settings.preview.timeline2"
                  defaultMessage="Diagnostic imaging and laboratory tests"
                />
              </li>
              <li>
                <FormattedMessage
                  id="settings.preview.timeline3"
                  defaultMessage="Treatment plan development"
                />
              </li>
            </ol>
            <h3
              style={lineHeightStyle}
              className="text-[12px] font-semibold mb-1 mt-2"
            >
              <FormattedMessage
                id="settings.preview.treatmentRecommendations"
                defaultMessage="Treatment Recommendations"
              />
            </h3>
            <div className="text-[8px] mb-1">
              <p style={lineHeightStyle} className="mb-0 mt-0">
                <FormattedMessage
                  id="settings.preview.treatmentIntro"
                  defaultMessage="Based on the comprehensive evaluation, the following treatment approach is recommended:"
                />
              </p>
              <ul style={lineHeightStyle} className="pl-3 space-y-0.5 mt-1">
                <li className="mb-0">
                  <FormattedMessage
                    id="settings.preview.treatment1"
                    defaultMessage="Physical therapy sessions (3x weekly)"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="settings.preview.treatment2"
                    defaultMessage="Pain management consultation"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="settings.preview.treatment3"
                    defaultMessage="Follow-up imaging in 6 weeks"
                  />
                </li>
              </ul>
            </div>
            <p style={lineHeightStyle} className="text-[8px] mb-2">
              <FormattedMessage
                id="settings.preview.conclusion"
                defaultMessage="With appropriate treatment and patient compliance, full recovery is expected within 12-16 weeks. Regular monitoring will ensure optimal outcomes."
              />
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 mt-2">
          <div className=" flex justify-between text-[6px] text-muted-foreground  pt-1">
            <p>
              <FormattedMessage
                id="settings.preview.poweredBy"
                defaultMessage={footnote ?? "Powered by Sky AI"}
              />
            </p>
            <p>
              <FormattedMessage
                id="settings.preview.pageCount"
                defaultMessage="1 of 1"
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
